import PropTypes from 'prop-types';
import React, { useCallback, useState, useEffect, useContext } from 'react';
import '../styles/QuestionsAndAnswersListItem.css';
import LoadingFallback from './LoadingFallback';
import Attachment from './Attachment';
import getHumanReadableDiff from '../helpers/getHumanReadableDiff';
import AbtraEditorContentDisplay from './AbtraEditorContentDisplay';
import AnswerVotes from './AnswerVotes';
import FollowQuestion from './FollowQuestion';
import QuestionReplyForm from './QuestionReplyForm';
import { ToastContext } from './context/ToastContext';

/** CONFIGURATION */
// The default number of answers to show in the component.
const DEFAULT_LIMIT = 10;

/**
 * Display a product's question and its answers.
 * @component
 */
const QuestionsAndAnswersListItem = ({ question }) => {
    const [answers, setAnswers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [answersLoaded, setAnswersLoaded] = useState(false);
    const { sendMessage } = useContext(ToastContext);

    /**
     * Get question answers from the API.
     */
    const fetchAnswers = useCallback(async () => {
        try {
            setLoading(true);
            const url = new URL(`/api/answers/${question.id}`, process.env.APP_URL);
            const params = new URLSearchParams({
                page: 0,
                limit: DEFAULT_LIMIT,
            });
            const response = await fetch(`${url}?${params}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
            });
            if (!response.ok) {
                throw new Error('Something went wrong.');
            }
            const data = await response.json();
            setAnswers(data.answers);
            setLoading(false);
        } catch (e) {
            sendMessage(e.message, 'danger');
        } finally {
            setLoading(false);
            setAnswersLoaded(true);
        }
    }, [question]);

    useEffect(() => {
        if (!answersLoaded) {
            fetchAnswers();
        }
    }, [fetchAnswers]);

    return (
        <div
            className={'question-wrapper'}
        >
            <div
                className={'question-container'}>
                <p
                    className={'qa-date-ago'}
                >
                    {getHumanReadableDiff(question.createdAt)}
                </p>
                <p
                    style={{ marginBottom: '0' }}
                >
                    <h5>{question.title}</h5>
                    <div
                        dangerouslySetInnerHTML={{ __html: question.question }}
                    />
                    <span
                        className={'qanda-item--muted-text'}> ({question.name})</span>
                </p>
                <div>
                    {question.Attachments.map((attachment) => {
                        return (
                            <Attachment
                                key={attachment.url}
                                attachment={attachment}
                            />
                        );
                    })}
                </div>
                <FollowQuestion
                    questionId={question.id}
                />
            </div>
            {loading && <LoadingFallback />}
            {(Array.isArray(answers) && answers.length > 0 && !loading)
                ? answers.map((answer, index) => {
                    return <div
                        className={'answer-container'}
                        key={`${answer.id}_${index}`}>
                        <p
                            className={'qa-date-ago'}
                        >
                            {getHumanReadableDiff(answer.createdAt)}
                        </p>
                        <AbtraEditorContentDisplay
                            classNames={'answer-text'}
                            htmlContent={answer.answer}
                        />
                        <div>
                            {answer.Attachments.map((attachment) => {
                                return (
                                    <Attachment
                                        key={attachment.url}
                                        attachment={attachment}
                                    />
                                );
                            })}
                        </div>
                        <AnswerVotes
                            answerId={answer.id}
                        />
                    </div>;
                })
                : null
            }
            <QuestionReplyForm
                questionId={question.id}
                fetchAnswers={fetchAnswers}
                hasAnswers={answers.length > 0}
            />
        </div>
    );
};

/** Prop Types */
QuestionsAndAnswersListItem.propTypes = {
    /** The question to display as a list item. */
    question: PropTypes.shape({
        /** The ID of the question. */
        id: PropTypes.number,
        /** The name of the person who asked the question. */
        name: PropTypes.string,
        /** The question. */
        title: PropTypes.string,
        question: PropTypes.string,
        /** The list of images for the question. */
        Attachments: PropTypes.arrayOf(PropTypes.shape({
            /** The URL of the image. */
            url: PropTypes.string,
            /** The alt text of the image. */
            altText: PropTypes.string,
        })),
        /** createdAt date. */
        createdAt: PropTypes.string,
        /** The list of answers for the question. */
        Answers: PropTypes.arrayOf(PropTypes.shape({
            /** The answer to the question. */
            answer: PropTypes.string,
        })),
    }),
};

export default QuestionsAndAnswersListItem;
